import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ShieldCheck, X, ArrowLeft } from '@phosphor-icons/react';

/**
 * Fixed top banner shown to logged-in admins when they are viewing a
 * role-scoped page (contractor / worker / homeowner dashboards or control
 * panels). Lets the admin know they're in "Admin View" mode and gives a
 * one-click exit back to /admin.
 *
 * Props:
 *   role : "contractor" | "worker" | "homeowner"  — the dashboard being viewed
 */
const AdminViewBanner = ({ role }) => {
  const navigate = useNavigate();
  const userType = (typeof window !== 'undefined' ? localStorage.getItem('userType') : '') || '';
  const backup = typeof window !== 'undefined' ? localStorage.getItem('admin_impersonation_backup') : null;
  const impersonating = !!backup && userType !== 'admin';

  // Impersonation is handled by the global banner (GlobalTestModeBanner)
  if (impersonating) return null;

  // Only show for admins or active impersonation sessions
  if (userType !== 'admin' && !impersonating) return null;

  let testUser = null;
  if (impersonating) {
    try { testUser = JSON.parse(localStorage.getItem('user') || 'null'); } catch { testUser = null; }
  }

  const endImpersonation = () => {
    try {
      const saved = JSON.parse(backup);
      localStorage.setItem('token', saved.token);
      localStorage.setItem('userType', saved.userType);
      localStorage.setItem('userId', saved.userId);
      localStorage.setItem('user', saved.user);
    } finally {
      localStorage.removeItem('admin_impersonation_backup');
      window.location.href = '/admin';
    }
  };

  const label =
    role === 'contractor' ? 'Contractor Dashboard' :
    role === 'worker' ? 'Worker Dashboard' :
    role === 'homeowner' ? 'Homeowner Dashboard' :
    'Role Dashboard';

  return (
    <div
      className="w-full bg-red-600 text-white border-b-4 border-black"
      data-testid="admin-view-banner"
    >
      <div className="max-w-7xl mx-auto px-4 py-2 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-7 h-7 bg-black flex items-center justify-center border-2 border-white flex-shrink-0">
            <ShieldCheck size={14} weight="fill" className="text-red-500" />
          </div>
          <span className="text-[10px] md:text-xs font-black uppercase tracking-widest">
            {impersonating ? 'Test Mode' : 'Admin View'}
          </span>
          <span className="text-white/60 text-xs hidden sm:inline">·</span>
          <span className="text-xs md:text-sm font-bold truncate">
            {impersonating
              ? `Testing as ${testUser?.name || testUser?.email || 'user'} (${userType})`
              : `Viewing ${label} as an admin`}
          </span>
        </div>
        <button
          onClick={impersonating ? endImpersonation : () => navigate('/admin')}
          className="inline-flex items-center gap-1.5 h-8 px-3 bg-black text-white font-black uppercase text-[10px] md:text-xs tracking-widest border-2 border-white shadow-[2px_2px_0px_#fff] hover:shadow-[3px_3px_0px_#fff] active:translate-y-0.5 active:shadow-none rounded-none transition-all flex-shrink-0"
          data-testid="admin-view-banner-exit"
        >
          <ArrowLeft size={12} weight="bold" />
          <span className="hidden sm:inline">Back to Admin</span>
          <X size={12} weight="bold" className="sm:hidden" />
        </button>
      </div>
    </div>
  );
};

export default AdminViewBanner;
